import { 
  collection, 
  addDoc, 
  getDoc,
  getDocs, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  doc,
  increment,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { AutomationStep } from './automationService';

/**
 * Automation Template Interface
 * Represents a pre-built automation workflow template that can be used by users
 */
export interface AutomationTemplate {
  id?: string;
  name: string;
  description: string;
  category: 'ecommerce' | 'promotions' | 'events' | 'special_occasions' | 'lead_engagement';
  type: string; // Optional subtitle/tagline
  goal: string; // Optional: "Convert Subscribers", "Recover Visitors", etc.
  channel: 'Email' | 'Email + SMS' | 'SMS' | 'Multi-channel'; // Optional
  steps: AutomationStep[]; // The actual workflow configuration
  estimatedDuration: string; // e.g., "1 day", "3 days", "1 week"
  tags: string[];
  badge?: string; // e.g., "High Order Rate", "Popular", "Recommended"
  isRecommended: boolean;
  isActive: boolean;
  previewImage?: string;
  
  // Admin metadata
  createdBy: string; // Admin user ID
  createdAt: any; // Timestamp
  updatedAt?: any; // Timestamp
  isPublic: boolean;
  isPremium: boolean;
  price: number; // 0 for free templates
  
  // Analytics & tracking
  usageCount: number;
  downloadCount: number;
  lastUsed?: any; // Timestamp
  successRate?: number; // Percentage of successful executions
  averageRevenue?: number; // Average revenue generated
  
  // SEO and discoverability
  searchKeywords?: string[];
  difficulty?: 'beginner' | 'intermediate' | 'advanced';
  industryFocus?: string[]; // e.g., ["ecommerce", "saas", "education"]
}

/**
 * Template Analytics Interface
 */
export interface TemplateAnalytics {
  templateId: string;
  totalUsage: number;
  totalDownloads: number;
  activeAutomations: number;
  averageSuccessRate: number;
  totalRevenue: number;
  popularityScore: number;
  lastUsed?: Date;
  usageByMonth: { month: string; count: number }[];
}

/**
 * Template Usage Record
 */
export interface TemplateUsage {
  id?: string;
  templateId: string;
  userId: string;
  automationId?: string;
  downloadedAt: any; // Timestamp
  activated: boolean;
  activatedAt?: any; // Timestamp
}

/**
 * Automation Template Service
 * Manages automation templates in Firestore
 */
class AutomationTemplateService {
  private templatesCollection = 'automationTemplates';
  private usageCollection = 'automationTemplateUsage';

  /**
   * Create a new automation template
   */
  async createTemplate(template: Omit<AutomationTemplate, 'id'>): Promise<string> {
    try {
      const templateData = {
        ...template,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        usageCount: template.usageCount || 0,
        downloadCount: template.downloadCount || 0,
        isActive: template.isActive !== undefined ? template.isActive : true,
        isPublic: template.isPublic !== undefined ? template.isPublic : true,
        isPremium: template.isPremium || false,
        price: template.price || 0,
        isRecommended: template.isRecommended || false,
      };

      const docRef = await addDoc(collection(db, this.templatesCollection), templateData);
      console.log('Automation template created with ID:', docRef.id);
      return docRef.id;
    } catch (error) {
      console.error('Error creating automation template:', error);
      throw error;
    }
  }

  /**
   * Get a single automation template by ID
   */
  async getTemplate(templateId: string): Promise<AutomationTemplate | null> {
    try {
      const docRef = doc(db, this.templatesCollection, templateId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() } as AutomationTemplate;
      }
      return null;
    } catch (error) {
      console.error('Error getting automation template:', error);
      throw error;
    }
  }

  /**
   * Get all automation templates
   */
  async getAllTemplates(): Promise<AutomationTemplate[]> {
    try {
      const q = query(
        collection(db, this.templatesCollection),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as AutomationTemplate[];
    } catch (error) {
      console.error('Error getting all automation templates:', error);
      throw error;
    }
  }

  /**
   * Get only active templates (for public display)
   */
  async getActiveTemplates(): Promise<AutomationTemplate[]> {
    try {
      console.log('🔍 Querying automationTemplates with filters: isActive=true, isPublic=true');
      const q = query(
        collection(db, this.templatesCollection),
        where('isActive', '==', true),
        where('isPublic', '==', true),
        orderBy('usageCount', 'desc')
      );
      const querySnapshot = await getDocs(q);
      console.log(`📊 Query returned ${querySnapshot.size} template(s)`);
      
      const templates = querySnapshot.docs.map(doc => {
        const data = doc.data();
        console.log(`   - ${data.name} (isActive: ${data.isActive}, isPublic: ${data.isPublic})`);
        return {
          id: doc.id,
          ...data
        };
      }) as AutomationTemplate[];
      
      return templates;
    } catch (error) {
      console.error('❌ Error getting active automation templates:', error);
      if (error instanceof Error) {
        console.error('   Error details:', error.message);
        // Check if it's a Firestore index error
        if (error.message.includes('index')) {
          console.error('   ⚠️  This might be a Firestore index issue!');
          console.error('   Follow the link in the error above to create the required index.');
        }
      }
      throw error;
    }
  }

  /**
   * Get recommended templates
   */
  async getRecommendedTemplates(): Promise<AutomationTemplate[]> {
    try {
      const q = query(
        collection(db, this.templatesCollection),
        where('isActive', '==', true),
        where('isRecommended', '==', true),
        orderBy('usageCount', 'desc')
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as AutomationTemplate[];
    } catch (error) {
      console.error('Error getting recommended templates:', error);
      throw error;
    }
  }

  /**
   * Get templates by category
   */
  async getTemplatesByCategory(category: string): Promise<AutomationTemplate[]> {
    try {
      const q = query(
        collection(db, this.templatesCollection),
        where('category', '==', category),
        where('isActive', '==', true),
        orderBy('usageCount', 'desc')
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as AutomationTemplate[];
    } catch (error) {
      console.error('Error getting templates by category:', error);
      throw error;
    }
  }

  /**
   * Search templates by name, description, or tags
   */
  async searchTemplates(searchTerm: string): Promise<AutomationTemplate[]> {
    try {
      // Get all active templates and filter client-side
      // For better performance, consider using Algolia or similar
      const templates = await this.getActiveTemplates();
      const lowerSearch = searchTerm.toLowerCase();
      
      return templates.filter(template => 
        template.name.toLowerCase().includes(lowerSearch) ||
        template.description.toLowerCase().includes(lowerSearch) ||
        template.tags.some(tag => tag.toLowerCase().includes(lowerSearch)) ||
        template.type.toLowerCase().includes(lowerSearch)
      );
    } catch (error) {
      console.error('Error searching templates:', error);
      throw error;
    }
  }

  /**
   * Update an automation template
   */
  async updateTemplate(templateId: string, updates: Partial<AutomationTemplate>): Promise<void> {
    try {
      const docRef = doc(db, this.templatesCollection, templateId);
      await updateDoc(docRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
      console.log('Automation template updated:', templateId);
    } catch (error) {
      console.error('Error updating automation template:', error);
      throw error;
    }
  }

  /**
   * Delete an automation template
   */
  async deleteTemplate(templateId: string): Promise<void> {
    try {
      const docRef = doc(db, this.templatesCollection, templateId);
      await deleteDoc(docRef);
      console.log('Automation template deleted:', templateId);
    } catch (error) {
      console.error('Error deleting automation template:', error);
      throw error;
    }
  }

  /**
   * Toggle template active status
   */
  async toggleTemplateActive(templateId: string, isActive: boolean): Promise<void> {
    try {
      await this.updateTemplate(templateId, { isActive });
    } catch (error) {
      console.error('Error toggling template active status:', error);
      throw error;
    }
  }

  /**
   * Duplicate a template
   */
  async duplicateTemplate(templateId: string, newName?: string): Promise<string> {
    try {
      const template = await this.getTemplate(templateId);
      if (!template) {
        throw new Error('Template not found');
      }

      const { id, createdAt, updatedAt, usageCount, downloadCount, lastUsed, ...templateData } = template;
      
      const duplicatedTemplate: Omit<AutomationTemplate, 'id'> = {
        ...templateData,
        name: newName || `${template.name} (Copy)`,
        usageCount: 0,
        downloadCount: 0,
        isActive: false, // Duplicates start as inactive
        createdAt: new Date() as any,
        updatedAt: new Date() as any,
      };

      return await this.createTemplate(duplicatedTemplate);
    } catch (error) {
      console.error('Error duplicating template:', error);
      throw error;
    }
  }

  /**
   * Record template usage
   */
  async recordTemplateUsage(templateId: string, userId: string, automationId?: string): Promise<void> {
    try {
      // Add usage record
      await addDoc(collection(db, this.usageCollection), {
        templateId,
        userId,
        automationId,
        downloadedAt: serverTimestamp(),
        activated: !!automationId,
        activatedAt: automationId ? serverTimestamp() : null
      });

      // Increment usage count
      const docRef = doc(db, this.templatesCollection, templateId);
      await updateDoc(docRef, {
        usageCount: increment(1),
        lastUsed: serverTimestamp()
      });

      console.log('Template usage recorded:', templateId);
    } catch (error) {
      console.error('Error recording template usage:', error);
      throw error;
    }
  }

  /**
   * Record template download
   */
  async recordTemplateDownload(templateId: string, userId: string): Promise<void> {
    try {
      // Add download record
      await addDoc(collection(db, this.usageCollection), {
        templateId,
        userId,
        downloadedAt: serverTimestamp(),
        activated: false
      });

      // Increment download count
      const docRef = doc(db, this.templatesCollection, templateId);
      await updateDoc(docRef, {
        downloadCount: increment(1),
        lastUsed: serverTimestamp()
      });

      console.log('Template download recorded:', templateId);
    } catch (error) {
      console.error('Error recording template download:', error);
      throw error;
    }
  }

  /**
   * Get template analytics
   */
  async getTemplateAnalytics(templateId: string): Promise<TemplateAnalytics> {
    try {
      const template = await this.getTemplate(templateId);
      if (!template) {
        throw new Error('Template not found');
      }

      // Get usage records
      const q = query(
        collection(db, this.usageCollection),
        where('templateId', '==', templateId)
      );
      const usageSnapshot = await getDocs(q);
      const usageRecords = usageSnapshot.docs.map(doc => doc.data() as TemplateUsage);

      const activeAutomations = usageRecords.filter(r => r.activated).length;
      
      // Calculate usage by month (simplified)
      const usageByMonth: { month: string; count: number }[] = [];
      
      return {
        templateId,
        totalUsage: template.usageCount || 0,
        totalDownloads: template.downloadCount || 0,
        activeAutomations,
        averageSuccessRate: template.successRate || 0,
        totalRevenue: template.averageRevenue || 0,
        popularityScore: this.calculatePopularityScore(template),
        lastUsed: template.lastUsed?.toDate(),
        usageByMonth
      };
    } catch (error) {
      console.error('Error getting template analytics:', error);
      throw error;
    }
  }

  /**
   * Calculate popularity score
   */
  private calculatePopularityScore(template: AutomationTemplate): number {
    const usageWeight = 0.4;
    const downloadWeight = 0.3;
    const successWeight = 0.3;

    const usageScore = Math.min((template.usageCount || 0) / 100, 1) * 100;
    const downloadScore = Math.min((template.downloadCount || 0) / 100, 1) * 100;
    const successScore = (template.successRate || 0);

    return (usageScore * usageWeight) + 
           (downloadScore * downloadWeight) + 
           (successScore * successWeight);
  }

  /**
   * Get popular templates (top 10)
   */
  async getPopularTemplates(limit: number = 10): Promise<AutomationTemplate[]> {
    try {
      const templates = await this.getActiveTemplates();
      return templates
        .sort((a, b) => this.calculatePopularityScore(b) - this.calculatePopularityScore(a))
        .slice(0, limit);
    } catch (error) {
      console.error('Error getting popular templates:', error);
      throw error;
    }
  }

  /**
   * Get templates by difficulty
   */
  async getTemplatesByDifficulty(difficulty: 'beginner' | 'intermediate' | 'advanced'): Promise<AutomationTemplate[]> {
    try {
      const q = query(
        collection(db, this.templatesCollection),
        where('difficulty', '==', difficulty),
        where('isActive', '==', true),
        orderBy('usageCount', 'desc')
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as AutomationTemplate[];
    } catch (error) {
      console.error('Error getting templates by difficulty:', error);
      throw error;
    }
  }

  /**
   * Bulk import templates
   */
  async bulkImportTemplates(templates: Omit<AutomationTemplate, 'id'>[]): Promise<string[]> {
    try {
      const templateIds: string[] = [];
      
      for (const template of templates) {
        const id = await this.createTemplate(template);
        templateIds.push(id);
      }
      
      console.log(`Bulk imported ${templateIds.length} templates`);
      return templateIds;
    } catch (error) {
      console.error('Error bulk importing templates:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const automationTemplateService = new AutomationTemplateService();

